import { useTranslation } from 'react-i18next';
import React, { useRef, useState } from 'react';
import { CircularProgress, IconButton, ListItemText, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import { useTokens } from 'theme';
import { exportPlan } from './chartExportPlan';

/** Filesystem names cannot contain control characters or path marks. */
export function exportName(title, extension, fallback) {
  // eslint-disable-next-line no-control-regex
  const name = (title || fallback).replace(/[<>:"/\\|?*\u0000-\u001f]/g, '_').trim().slice(0, 120);
  return `${name || fallback}.${extension}`;
}

function save(blob, name) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = name;
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/** A card as a file for a slide, built from the rows already on screen. */
export default function ChartExportMenu({ widget, columns, rows, meta }) {
  const { t: translate } = useTranslation();
  const t = useTokens();
  const { fontFamily } = useTheme().typography;
  const anchor = useRef(null);
  const running = useRef(false);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState(null);

  const buildPlan = () => exportPlan({ widget, columns, rows, meta, themeId: t.chart.id, fontFamily });

  const offer = () => {
    if (running.current) return;
    // A chart the data turned into a table has nothing to draw, and only opening
    // the menu pays for finding that out.
    const drawable = Boolean(buildPlan());
    setProblem(drawable ? null : 'assistant.exportUnavailable');
    setOpen(drawable);
  };

  const download = async (format) => {
    if (running.current) return;
    running.current = true;
    setOpen(false);
    setBusy(true);
    setProblem(null);
    try {
      const { renderPng, renderSvg } = await import('./chartExport');
      const blob = await (format === 'png' ? renderPng : renderSvg)(buildPlan());
      save(blob, exportName(widget.title, format, translate('assistant.exportChart')));
    } catch (error) {
      setProblem('assistant.exportFailed');
    } finally {
      running.current = false;
      setBusy(false);
    }
  };

  return <>
    {problem && <Typography variant="caption" role="alert" sx={{ color: t.inkMuted, whiteSpace: 'nowrap' }}>{translate(problem)}</Typography>}
    <Tooltip title={translate('assistant.exportChart')}>
      <IconButton ref={anchor} size="small" aria-label={translate('assistant.exportChart')} aria-haspopup="menu" aria-expanded={open} aria-busy={busy} onClick={offer}>
        {busy ? <CircularProgress size={16} /> : <ImageOutlinedIcon fontSize="small" />}
      </IconButton>
    </Tooltip>
    <Menu anchorEl={anchor.current} open={open} onClose={() => setOpen(false)}>
      <MenuItem onClick={() => download('png')}><ListItemText primary={translate('assistant.exportPng')} secondary={translate('assistant.exportPngHint')} /></MenuItem>
      <MenuItem onClick={() => download('svg')}><ListItemText primary={translate('assistant.exportSvg')} secondary={translate('assistant.exportSvgHint')} /></MenuItem>
    </Menu>
  </>;
}
